import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Icon,
  PasswordInput,
  Text,
} from "@salestrak/ui";
import { PASSWORD_MIN_LENGTH, newPasswordProblem, resetPassword } from "#app/lib/auth-service";
import { useLocalSearchParams, useRouter } from "#app/lib/hooks/router-compat";
import { useSplashAutoFocus } from "#app/lib/splash-context";
import { AnalyticsContext, AuthEvent, trackEvent } from "#app/lib/analytics";

export function ResetPasswordForm() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const router = useRouter();
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [confirmError, setConfirmError] = React.useState("");
  const [formError, setFormError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const passwordRef = useSplashAutoFocus();

  // A link with no token can never succeed, so the page says so on arrival
  // and offers a new link, rather than after a password is typed.
  const missingToken = !token;

  const submit = async () => {
    if (!token) return;
    setFormError("");
    if (!password.trim()) {
      setPasswordError("Password is required.");
      return;
    }
    const problem = newPasswordProblem(password);
    if (problem) {
      setPasswordError(problem);
      return;
    }
    if (password !== confirmPassword) {
      setConfirmError("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword(token, password);
      trackEvent({ context: AnalyticsContext.Auth, event: AuthEvent.PasswordReset, label: "success" });
      router.replace("/(auth)/login");
    } catch (err: any) {
      trackEvent({ context: AnalyticsContext.Auth, event: AuthEvent.PasswordReset, label: "failure" });
      setFormError(err.message ?? "Reset failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit();
  };

  if (missingToken) {
    return (
      <Box direction="column" gap="lg">
        <Alert variant="error">
          <Icon name="alert-circle" />
          <AlertTitle>This reset link is invalid or incomplete.</AlertTitle>
        </Alert>
        <Box direction="column" paddingTop="lg">
          <Button size="xl" onClick={() => router.replace("/(auth)/forgot-password")}>
            Request a new link
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box as="form" noValidate direction="column" gap="lg" onSubmit={handleSubmit}>
      <Box gap="base">
        <Text
          as="label"
          htmlFor="reset-password"
          size="sm"
          weight="medium"
          variant="muted"
        >
          New Password
        </Text>
        <Box gap="sm">
          <PasswordInput
            id="reset-password"
            size="xl"
            placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError("");
              setConfirmError("");
            }}
            aria-invalid={!!passwordError}
            ref={passwordRef}
            autoComplete="new-password"
            disabled={isLoading}
          />
          {passwordError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {passwordError}
            </Text>
          ) : null}
        </Box>
      </Box>

      <Box gap="base">
        <Text
          as="label"
          htmlFor="reset-confirm"
          size="sm"
          weight="medium"
          variant="muted"
        >
          Confirm Password
        </Text>
        <Box gap="sm">
          <PasswordInput
            id="reset-confirm"
            size="xl"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setConfirmError("");
            }}
            aria-invalid={!!confirmError}
            autoComplete="new-password"
            disabled={isLoading}
          />
          {confirmError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {confirmError}
            </Text>
          ) : null}
        </Box>
      </Box>

      {formError ? (
        <Alert variant="error">
          <Icon name="alert-circle" />
          <AlertTitle>{formError}</AlertTitle>
        </Alert>
      ) : null}

      <Box direction="column" paddingTop="lg">
        <Button type="submit" size="xl" loading={isLoading}>
          Reset password
        </Button>
      </Box>
    </Box>
  );
}
